/**
 * The wording of the conversation close button and of the banner that follows a press.
 *
 * Kept apart from the hook so the shipped text is one exported value a test can lock and a
 * story can import, rather than a string a copy of the UI has to retype and can drift from.
 *
 * Each role's button names what its press does. For the author it closes the inquiry. For a
 * recipient, usually the expert, it says they have answered and asks the author to close it,
 * which the author then confirms, so the button reads "Mark as answered" and the banner that
 * follows "Marked as answered". "Archive" was the label for both, and a reader took it for
 * filing the case away, unsure whether the other doctor still saw their answer.
 *
 * The short label is the word that fits a phone's web header beside the participant's name.
 * "Mark answered" pushed a 360px page sideways; "Done" fits, and the banner that follows says
 * what it did. Native shows the full label, since its action sits in a menu row.
 *
 * The Ask Anonymously recipient gets its own set because the press does something different
 * for that role. The inquiry leaves the staff member's open inbox and lands in Closed, yet
 * stays active, so the submitter can still continue it and bring it back. "Archive" read as
 * final and "Hide" promised more than the press delivers, so this set names what the staff
 * member did, which is to mark the inquiry handled.
 */
export type CloseInquiryCopy = {
  buttonDesktop: string;
  buttonMobile: string;
  pendingTitle: string;
  pendingBody: string;
  pendingAction: string;
};

const PENDING_REQUEST_COPY = {
  pendingTitle: 'Request to close pending',
  pendingBody: 'Waiting on the inquiry author to continue or close the inquiry.',
  pendingAction: 'Cancel request',
} as const;

export const CLOSE_INQUIRY_COPY = {
  author: {
    buttonDesktop: 'Close inquiry',
    buttonMobile: 'Close',
    ...PENDING_REQUEST_COPY,
  },
  recipient: {
    buttonDesktop: 'Mark as answered',
    buttonMobile: 'Done',
    pendingTitle: 'Marked as answered',
    pendingBody: 'Waiting on the inquiry author to continue or close the inquiry.',
    pendingAction: 'Undo',
  },
  askAnonymouslyRecipient: {
    buttonDesktop: 'Mark handled',
    buttonMobile: 'Handled',
    pendingTitle: 'Marked handled',
    pendingBody: 'Moved to Closed. It returns to your inbox if the submitter replies.',
    pendingAction: 'Move back to inbox',
  },
} as const satisfies Record<string, CloseInquiryCopy>;

export const getCloseInquiryCopy = ({
  isAuthor,
  isAskAnonymouslyRecipient,
}: {
  isAuthor: boolean;
  isAskAnonymouslyRecipient: boolean;
}): CloseInquiryCopy => {
  if (isAskAnonymouslyRecipient) {
    return CLOSE_INQUIRY_COPY.askAnonymouslyRecipient;
  }

  return isAuthor ? CLOSE_INQUIRY_COPY.author : CLOSE_INQUIRY_COPY.recipient;
};
